// BMP_EMAIL_SELECTED_ORDER_ATTACHMENTS_V3
import { useState } from 'react';
import { FileCheck2, Paperclip, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useOrderDocuments } from '../../features/order-documents/api';
import {
  useOrderInvoiceAttachments,
  useAttachOrderInvoiceDocument,
} from '../../features/invoicing/invoiceAttachmentsApi';
import './InvoiceDocumentPicker.scss';

type Document = {
  id: string;
  fileName: string;
  sizeBytes: number;
  type: string;
  orderItemId?: string | null;
};
type Props = {
  orderId: string;
  quickBooksInvoiceId?: string | null;
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
};
const supported = new Set([
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'tif',
  'tiff',
  'doc',
  'xls',
  'xlsx',
  'csv',
  'xml',
]);
function permitted(document: Document) {
  const ext = document.fileName.split('.').pop()?.toLowerCase() ?? '';
  return supported.has(ext) && document.sizeBytes > 0 && document.sizeBytes <= 15 * 1024 * 1024;
}
export function InvoiceDocumentPicker({
  orderId,
  quickBooksInvoiceId,
  selectedIds,
  onSelectedIdsChange,
}: Props) {
  const documentsQuery = useOrderDocuments(orderId);
  const attachedQuery = useOrderInvoiceAttachments(orderId, Boolean(quickBooksInvoiceId));
  const upload = useAttachOrderInvoiceDocument();
  const [working, setWorking] = useState(false);
  const documents: Document[] = documentsQuery.data?.documents ?? [];
  const uploaded = new Set(
    (attachedQuery.data?.attachments ?? [])
      .filter((entry) => entry.status === 'UPLOADED')
      .map((entry) => entry.orderDocumentId)
  );
  const available = documents.filter(
    (document) => permitted(document) && !uploaded.has(document.id)
  );
  const eligibleSelected = selectedIds.filter((id) => available.some((d) => d.id === id));

  async function attachSelected() {
    if (!quickBooksInvoiceId || !eligibleSelected.length || working) return;
    setWorking(true);
    const errors: string[] = [];
    let success = 0;
    const remaining = [...selectedIds];
    for (const documentId of eligibleSelected) {
      try {
        await upload.mutateAsync({ orderId, documentId });
        success += 1;
        remaining.splice(remaining.indexOf(documentId), 1);
      } catch (err: any) {
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Upload failed';
        errors.push(
          `${documents.find((d) => d.id === documentId)?.fileName ?? 'Document'}: ${message}`
        );
      }
    }
    onSelectedIdsChange(remaining);
    await attachedQuery.refetch();
    if (success)
      toast.success(
        `${success} document${success === 1 ? '' : 's'} attached to the existing QuickBooks invoice.`
      );
    if (errors.length) toast.error(errors.join('\n'), { autoClose: false });
    setWorking(false);
  }

  if (documentsQuery.isLoading)
    return (
      <section className="invoice-documents">
        <Paperclip size={16} /> Loading order documents…
      </section>
    );
  return (
    <section className="invoice-documents" aria-label="Invoice attachments">
      <div className="invoice-documents__heading">
        <div>
          <strong>
            <Paperclip size={16} /> Attach order documents
          </strong>
          <small>
            Select which order documents should be attached and included when QuickBooks emails this
            invoice.
          </small>
        </div>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => onSelectedIdsChange(available.map((d) => d.id))}
          disabled={!available.length || working}
        >
          Select all eligible
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => onSelectedIdsChange([])}
          disabled={!selectedIds.length || working}
        >
          Clear
        </button>
      </div>
      {!documents.length && (
        <p className="invoice-documents__hint">
          No order documents available. Upload documents in Manage Load first.
        </p>
      )}
      {documents.map((document) => {
        const synced = uploaded.has(document.id);
        const isSupported = permitted(document);
        const blocked = (attachedQuery.data?.attachments ?? []).some(
          (x) => x.orderDocumentId === document.id && x.status === 'REVIEW_REQUIRED'
        );
        return (
          <label
            key={document.id}
            className={`invoice-documents__file ${synced ? 'invoice-documents__file--synced' : ''}`}
          >
            <input
              type="checkbox"
              checked={synced || selectedIds.includes(document.id)}
              disabled={!isSupported || synced || blocked || working}
              onChange={(event) =>
                onSelectedIdsChange(
                  event.target.checked
                    ? [...selectedIds, document.id]
                    : selectedIds.filter((x) => x !== document.id)
                )
              }
            />
            <span className="invoice-documents__filename">
              {document.fileName}
              <small>
                {document.type === 'DELIVERY_TICKET' ? 'Delivery ticket' : 'Order document'} ·{' '}
                {(document.sizeBytes / 1024 / 1024).toFixed(2)} MB
              </small>
            </span>
            {synced ? (
              <span className="invoice-documents__synced">
                <FileCheck2 size={14} /> Attached
              </span>
            ) : blocked ? (
              <span className="invoice-documents__blocked">Check QBO before retrying</span>
            ) : !isSupported ? (
              <span className="invoice-documents__blocked">Unsupported QBO file format/size</span>
            ) : null}
          </label>
        );
      })}
      {!!eligibleSelected.length && !quickBooksInvoiceId && (
        <p className="invoice-documents__hint">
          <AlertCircle size={15} /> Choose Create & send: BMP will create the invoice, attach these
          files, and email it only after every upload succeeds.
        </p>
      )}
      {!!eligibleSelected.length && quickBooksInvoiceId && (
        <button
          type="button"
          className="btn btn--secondary"
          disabled={working || upload.isPending}
          onClick={attachSelected}
        >
          <Paperclip size={15} />{' '}
          {working ? 'Attaching…' : `Attach selected (${eligibleSelected.length})`}
        </button>
      )}
      <p className="invoice-documents__hint">
        QuickBooks includes selected files in the invoice email using IncludeOnSend. Files attached
        after an earlier email require sending that invoice again; test with a sandbox customer
        before production.
      </p>
    </section>
  );
}
